// src/pages/PublicReviews.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaCommentDots,
  FaFilter,
  FaRedo,
  FaSearch,
  FaShieldAlt,
  FaSpinner,
  FaStar,
  FaStore,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { fetchPublicExperienceReviews, type PublicExperienceReview, type PublicReviewSummary } from '../lib/publicReviews';

const emptySummary: PublicReviewSummary = {
  count: 0,
  average_rating: 0,
  distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
  latest_at: null,
};

const sourceLabels: Record<string, string> = {
  all: 'Toutes les expériences',
  voucher: 'Vouchers / billets',
  reservation: 'Réservations',
  gift: 'Cartes & cadeaux',
  other: 'Autres expériences',
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fr-CA', { dateStyle: 'medium' }).format(date);
};

const Stars = ({ rating, size = 'text-base' }: { rating: number; size?: string }) => {
  const value = Math.round(Math.max(0, Math.min(5, Number(rating || 0))));
  return (
    <div className={`flex items-center gap-1 ${size}`} aria-label={`${value} étoiles sur 5`}>
      {Array.from({ length: 5 }).map((_, index) => (
        <FaStar key={index} className={index < value ? 'text-amber-400' : 'text-slate-200'} />
      ))}
    </div>
  );
};

export default function PublicReviews() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviews, setReviews] = useState<PublicExperienceReview[]>([]);
  const [summary, setSummary] = useState<PublicReviewSummary>(emptySummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(searchParams.get('region') || '');
  const [sourceType, setSourceType] = useState(searchParams.get('source') || 'all');
  const [minRating, setMinRating] = useState(Number(searchParams.get('min') || 0));

  const featuredReviews = useMemo(() => reviews.filter((review) => review.featured).slice(0, 3), [reviews]);

  const loadReviews = async () => {
    setLoading(true);
    setError(null);

    const payload = await fetchPublicExperienceReviews({
      region: search || null,
      sourceType: sourceType === 'all' ? null : sourceType,
      minRating: minRating || null,
      limit: 60,
    });

    if (!payload.ok) {
      setError(payload.details || payload.error || 'Impossible de charger les avis publics.');
      setReviews([]);
      setSummary(emptySummary);
    } else {
      setReviews(payload.reviews);
      setSummary(payload.summary || emptySummary);
    }

    const next = new URLSearchParams();
    if (search.trim()) next.set('region', search.trim());
    if (sourceType !== 'all') next.set('source', sourceType);
    if (minRating) next.set('min', String(minRating));
    setSearchParams(next, { replace: true });
    setLoading(false);
  };

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <SeoHead
        title="Avis publics Forfeo | Expériences locales vérifiées"
        description="Consultez les avis publics approuvés par Forfeo sur les expériences locales, vouchers, réservations et partenaires."
        canonicalPath="/avis-publics"
      />
      <Header />

      <main>
        <section className="relative overflow-hidden bg-slate-950 px-4 py-20 text-white">
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(59,130,246,.8), transparent 30%), radial-gradient(circle at 80% 0%, rgba(16,185,129,.7), transparent 30%)' }} />
          <div className="relative mx-auto max-w-7xl">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.25em] text-emerald-200">
                <FaShieldAlt /> Avis approuvés par Forfeo
              </div>
              <h1 className="text-4xl md:text-7xl font-black tracking-tighter leading-tight">La réputation locale, visible et vérifiée.</h1>
              <p className="mt-6 max-w-3xl text-base md:text-xl font-semibold leading-relaxed text-slate-300">
                Les avis publics Forfeo sont modérés avant diffusion. Ils aident les voyageurs à choisir avec confiance et donnent aux partenaires une preuve sociale de qualité.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/avis-experience" className="inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-950 hover:bg-emerald-50 transition">
                  Laisser un avis <FaArrowRight />
                </Link>
                <Link to="/decouvrir-quebec" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-6 py-4 text-xs font-black uppercase tracking-widest text-white hover:bg-white/15 transition">
                  Découvrir le Québec
                </Link>
              </div>
            </motion.div>

            <div className="mt-12 grid gap-4 md:grid-cols-4">
              {[
                ['Avis publics', summary.count || 0],
                ['Note moyenne', summary.average_rating ? `${summary.average_rating.toFixed(1)}★` : '—'],
                ['Avis 5★', summary.distribution?.['5'] || 0],
                ['Modération', 'Forfeo'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-[1.5rem] border border-white/10 bg-white/10 p-5 backdrop-blur-md">
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">{label}</p>
                  <p className="mt-2 text-3xl font-black text-white">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid gap-3 lg:grid-cols-[1fr_220px_180px_auto]">
              <label className="relative block">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') loadReviews(); }}
                  placeholder="Filtrer par région, partenaire ou ville..."
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold outline-none focus:border-emerald-400"
                />
              </label>

              <label className="relative block">
                <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <select
                  value={sourceType}
                  onChange={(event) => setSourceType(event.target.value)}
                  className="h-12 w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-black outline-none focus:border-emerald-400"
                >
                  {Object.entries(sourceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>

              <select
                value={minRating}
                onChange={(event) => setMinRating(Number(event.target.value))}
                className="h-12 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-black outline-none focus:border-emerald-400"
              >
                <option value={0}>Toutes notes</option>
                <option value={5}>5 étoiles</option>
                <option value={4}>4+ étoiles</option>
                <option value={3}>3+ étoiles</option>
              </select>

              <button onClick={loadReviews} disabled={loading} className="h-12 rounded-2xl bg-slate-950 px-6 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-700 disabled:opacity-60">
                {loading ? <FaSpinner className="mx-auto animate-spin" /> : 'Filtrer'}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800">
              {error}
            </div>
          )}

          {featuredReviews.length > 0 && (
            <section className="mt-10">
              <h2 className="mb-4 text-2xl font-black tracking-tight">Avis en vedette</h2>
              <div className="grid gap-5 lg:grid-cols-3">
                {featuredReviews.map((review) => <ReviewCard key={review.id} review={review} featured />)}
              </div>
            </section>
          )}

          <section className="mt-10">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black tracking-tight">Tous les avis publics</h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">Affichage des avis approuvés et publics seulement.</p>
              </div>
              <button onClick={loadReviews} className="hidden sm:inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-700 hover:bg-slate-50">
                <FaRedo /> Rafraîchir
              </button>
            </div>

            {loading ? (
              <div className="rounded-[2rem] border border-slate-200 bg-white p-10 text-center shadow-sm">
                <FaSpinner className="mx-auto mb-4 text-3xl text-emerald-500 animate-spin" />
                <p className="font-black text-slate-700">Chargement des avis publics...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
                <FaCommentDots className="mx-auto mb-4 text-4xl text-slate-300" />
                <h3 className="text-2xl font-black text-slate-950">Aucun avis public trouvé.</h3>
                <p className="mx-auto mt-2 max-w-xl text-sm font-semibold text-slate-500">
                  Essaie de retirer les filtres ou laisse le premier avis public après ton expérience Forfeo.
                </p>
                <Link to="/avis-experience" className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-700 transition">
                  Laisser un avis <FaArrowRight />
                </Link>
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {reviews.map((review) => <ReviewCard key={review.id} review={review} />)}
              </div>
            )}
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function ReviewCard({ review, featured = false }: { review: PublicExperienceReview; featured?: boolean }) {
  return (
    <article className={`rounded-[2rem] border bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${featured ? 'border-amber-200 ring-4 ring-amber-50' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <Stars rating={review.rating} size="text-lg" />
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">Approuvé</span>
      </div>
      <h3 className="mt-4 text-xl font-black leading-tight text-slate-950">{review.review_title || 'Expérience Forfeo vérifiée'}</h3>
      <p className="mt-3 text-sm font-semibold leading-relaxed text-slate-600">{review.comment}</p>

      <div className="mt-5 rounded-2xl bg-slate-50 p-4">
        <div className="flex items-center justify-between gap-3 text-xs font-bold text-slate-500">
          <span className="inline-flex items-center gap-2 text-slate-700"><FaCheckCircle className="text-emerald-500" /> {review.customer_display_name}</span>
          <span>{formatDate(review.created_at)}</span>
        </div>
        {(review.business_name || review.business_region) && (
          <p className="mt-3 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-blue-600">
            <FaStore /> {[review.business_name, review.business_region].filter(Boolean).join(' · ')}
          </p>
        )}
        <p className="mt-2 text-xs font-black uppercase tracking-widest text-slate-400">{review.source_label}</p>
      </div>

      {review.partner_response && (
        <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm font-semibold leading-relaxed text-blue-900">
          <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-blue-500">Réponse partenaire</p>
          {review.partner_response}
        </div>
      )}
    </article>
  );
}
